
import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Form, Input, Button, Select, InputNumber, Typography, Spin, Alert } from 'antd';
import { debounce } from 'lodash';
import { Liability, Asset, PostLiability } from '../../types';
import { AssetService } from '../assets/asset.service';

const { Title } = Typography;
const { Option } = Select;

interface AddEditLiabilityFormProps {
  visible: boolean;
  onClose: () => void;
  initialData?: Liability;
  onSubmit: (data: Liability) => void;
  estateId: string;
}

const AddEditLiabilityForm: React.FC<AddEditLiabilityFormProps> = ({
  visible,
  onClose,
  initialData,
  onSubmit,
  estateId,
}) => {
  const [form] = Form.useForm();
  const [assetOptions, setAssetOptions] = useState<Asset[]>([]);
  const [fetching, setFetching] = useState<boolean>(false);
  const [fetchError, setFetchError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const isEditing = !!initialData;

  useEffect(() => {
    if (initialData) {
      form.setFieldsValue({
        description: initialData.description,
        monthlyPayment: parseFloat(initialData.amount),
        source: initialData.source,
        name: initialData.name,
      });
    } else {
      form.resetFields();
    }
  }, [initialData, form]);

  // Debounced function to fetch assets
  const fetchAssetOptions = useCallback(
    debounce(async (value: string) => {
      if (!value) {
        setAssetOptions([]);
        setFetching(false);
        return;
      }
      setFetching(true);
      setFetchError('');
      try {
        const assets = await AssetService.searchAssets(estateId, value);
        setAssetOptions(assets);
      } catch (error: any) {
        console.error('Error fetching assets:', error);
        setFetchError(error.message || 'Failed to fetch assets.');
      } finally {
        setFetching(false);
      }
    }, 500), // 500ms debounce
    [estateId],
  );

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    fetchAssetOptions(value);
  };

  const handleSelect = (value: number) => {
    // Additional logic on select, if needed
  };

  const handleFinish = (values: any) => {
    const liability: Liability = {
      uid: initialData?.uid || '', 
      estateId: estateId,
      asset: values.assetId || initialData?.asset, // Use existing asset if not updated
      description: values.description || initialData?.description,
      amount: values.monthlyPayment !== undefined ? values.monthlyPayment : initialData?.amount,
      source: values.source || initialData?.source,
      name: values.name || initialData?.name,
    };
    onSubmit(liability);
    // Optionally reset fields or close modal
    // form.resetFields();
    // onClose();
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      title={isEditing ? 'Edit Liability' : 'Add Liability'}
      destroyOnClose
      width={600}
      bodyStyle={{ padding: '24px' }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        // initialValues={initialData || { monthlyPayment: 0 }}
      >
        <Title level={5}>Liability Details</Title>

        <Form.Item
          label="Name"
          name="name"
          rules={
            isEditing
              ? [] // No validation rules in edit mode
              : [{ required: true, message: 'Enter a name for the liability' }]
          }
        >
          <Input placeholder="Name of the liability" />
        </Form.Item>

        {fetchError && <Alert message={fetchError} type="error" showIcon style={{ marginBottom: '16px' }} />}

        <Form.Item
          label="Associated Asset"
          name="assetId"
          rules={
            isEditing
              ? []
              : [{ required: true, message: 'Please select an asset' }]
          }
        >
          <Select
            showSearch
            placeholder="Search and select an asset"
            notFoundContent={fetching ? <Spin size="small" /> : null}
            filterOption={false} // Disable built-in filtering to use server-side search
            onSearch={handleSearch}
            onSelect={handleSelect}
            allowClear
            loading={fetching}
          >
            {assetOptions.map((asset) => (
              <Option key={asset.uid} value={asset.uid}>
                {asset.name} - {asset.description}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
          rules={
            isEditing
              ? []
              : [{ required: true, message: 'Please enter a description' }]
          }
        >
          <Input.TextArea rows={3} placeholder="Enter liability description" />
        </Form.Item>

        <Form.Item
          label="Monthly Payment"
          name="monthlyPayment"
          rules={
            isEditing
              ? [
                  { type: 'number', min: Number.NEGATIVE_INFINITY, message: 'Monthly Payment must be a number' },
                ]
              : [
                  { required: true, message: 'Please enter the monthly payment' },
                  { type: 'number', min: Number.NEGATIVE_INFINITY, message: 'Monthly Payment must be a number' },
                ]
          }
        >
          <InputNumber style={{ width: '100%' }} placeholder="e.g., 500" />
        </Form.Item>

        <Form.Item
          label="Source"
          name="source"
          rules={
            isEditing
              ? []
              : [{ required: true, message: 'Please enter the source' }]
          }
        >
          <Input placeholder="Source of liability" />
        </Form.Item>

        {/* Add more fields as necessary */}

        <Form.Item>
          <Button type="primary" htmlType="submit" style={{ marginRight: '8px' }}>
            {isEditing ? 'Update Liability' : 'Add Liability'}
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddEditLiabilityForm;
